import * as React from "react";

import type { CatalogItemImageInput } from "@/lib/catalog-item-image";

import { HeroImageBox } from "./hero-image-box";
import { DownArrow, SpokenOldPicture } from "./old-beside-new";

// The changed-from-base highlight's ghost picture — #1043's variant E for an
// item swap, and D4's for a line turned per-plan or back (ledger §7, "Changed
// from base widens"): the base side's picture small, grey and struck, a
// centred ↓, then the current picture drawn as without the switch.
//
//   - **the ghost** — the spec rows' 72px picture box scaled into 40px,
//     greyscale at 60% opacity, a grey stroke corner to corner; hidden from
//     assistive technology, which hears "Base item picture: <name>. Now: "
//     in its place and then the current picture by its own alt text (in a
//     push's preview, "Picture now: <name>. After the push: " — the
//     `PairWording`'s words, #1090);
//   - **the ↓** — the pair's arrow, vertical.
//
// An Atlas addition with no donor, beside `OldBesideNew` (`old-beside-new.tsx`).

/** The ghost's box, in px; the 72px picture box is scaled down into it. */
const GHOST_SIZE = 40;
const HERO_SIZE = 72;

export function BaseItemPicture({
  image,
  perPlan = false,
  name,
  children,
}: {
  /** The base item's hero, or nothing. */
  image: CatalogItemImageInput | null | undefined;
  /** The base side was per-plan: the per-plan illustration is the ghost. */
  perPlan?: boolean;
  /** What the announcement names the base picture by. */
  name: string;
  /** The current picture, as drawn without the switch. */
  children: React.ReactNode;
}) {
  return (
    // A block as wide as the current picture, at the cell's left as the
    // picture box alone sits (a block, which the cell's centring never
    // moves): the ghost and the ↓ centre over the picture, which stays put.
    <span className="flex w-fit flex-col items-center gap-1">
      <span
        aria-hidden="true"
        data-testid="base-item-picture"
        className="pointer-events-none relative block shrink-0 overflow-hidden opacity-60 grayscale"
        style={{ width: GHOST_SIZE, height: GHOST_SIZE }}
      >
        <span
          className="block origin-top-left"
          style={{ transform: `scale(${GHOST_SIZE / HERO_SIZE})` }}
        >
          <HeroImageBox image={image} perPlan={perPlan} alt="" />
        </span>
        {/* The strike, bottom-left to top-right, in the pair's grey. */}
        <span className="absolute inset-0 bg-[linear-gradient(to_top_right,transparent_calc(50%-1px),var(--color-mui-grey-700)_calc(50%-1px),var(--color-mui-grey-700)_calc(50%+1px),transparent_calc(50%+1px))]" />
      </span>
      <SpokenOldPicture name={name} />
      <DownArrow />
      {children}
    </span>
  );
}
